import { Application_Status_Enum } from '../gql'

export const canAcceptApplication = (
  isAdmin: boolean,
  someApplicationIsAccepted: boolean,
  application: {
    status: Application_Status_Enum
    archived: boolean
  } | null
) => {
  return (
    isAdmin &&
    application &&
    !application.archived &&
    application.status === Application_Status_Enum.Submitted &&
    !someApplicationIsAccepted
  )
}

export const canCancelApplication = (
  isAdmin: boolean,
  application: {
    status: Application_Status_Enum
    archived: boolean
  }
) => {
  return (
    isAdmin &&
    application &&
    !application.archived &&
    application.status === Application_Status_Enum.Submitted
  )
}

export const canUnacceptApplication = (
  isAdmin: boolean,
  application: {
    status: Application_Status_Enum
    archived: boolean
    authenticActSignatureDate?: string | null
  } | null
) => {
  return (
    isAdmin &&
    application &&
    !application.archived &&
    application.status === Application_Status_Enum.Accepted &&
    application.authenticActSignatureDate == null
  )
}

export const isPromiseOfSaleSigned = (application: {
  status: Application_Status_Enum
  promiseOfSaleSignatureDate?: string | null
  promiseOfSalesFileId?: string | null
}) => {
  return (
    application.status === Application_Status_Enum.Accepted &&
    application.promiseOfSaleSignatureDate != null &&
    application.promiseOfSalesFileId != null
  )
}

export const isAuthenticActSigned = (application: {
  status: Application_Status_Enum
  authenticActSignatureDate?: string | null
  authenticActFileId?: string | null
}) => {
  return (
    application.status === Application_Status_Enum.Accepted &&
    application.authenticActSignatureDate != null &&
    application.authenticActFileId != null
  )
}

export const canSignPromiseOfSale = (
  isAdmin: boolean,
  application: {
    status: Application_Status_Enum
    promiseOfSaleSignatureDate?: string | null
    promiseOfSalesFileId?: string | null
  }
) => {
  return (
    isAdmin &&
    application.status === Application_Status_Enum.Accepted &&
    !isPromiseOfSaleSigned(application) &&
    !isAuthenticActSigned(application)
  )
}

export const canSignAuthenticAct = (
  isAdmin: boolean,
  application: {
    status: Application_Status_Enum
    promiseOfSaleSignatureDate?: string | null
    promiseOfSalesFileId?: string | null
  }
) => {
  return (
    isAdmin &&
    application.status === Application_Status_Enum.Accepted &&
    !isAuthenticActSigned(application) &&
    isPromiseOfSaleSigned(application)
  )
}

/**
 * It makes sense for the `application.status` to be restrained to a few values (draft, submitted, accepted, cancelled), as we have other columns to calculate a more "complete" status
 * This should be moved in Hasura, but creating a SQL function for this kind of logic is not something I'd like to do nor maintain
 * Instead, we use this helper on backend + frontends
 */
export type ApplicationEnrichedStatus =
  | `Draft`
  | `Submitted`
  | `PreAccepted`
  | `UnderPromise`
  | `ActSigned`
  | `Cancelled`
  | `Booked`

export const getApplicationEnrichedStatus = (application: {
  status: Application_Status_Enum
  promiseOfSaleSignatureDate?: string | null
  promiseOfSalesFileId?: string | null
  buyer_member: {
    visits: {
      id: string
    }[]
  }
  hasDraftBeenEdited: boolean
}): ApplicationEnrichedStatus => {
  const actSigned = isAuthenticActSigned(application)
  const underPromise = isPromiseOfSaleSigned(application)

  if (actSigned) return 'ActSigned'

  if (underPromise && !actSigned) return 'UnderPromise'

  if (application.status === Application_Status_Enum.Accepted) {
    return 'PreAccepted'
  }

  if (application.status === Application_Status_Enum.Cancelled) {
    return 'Cancelled'
  }

  if (application.status === Application_Status_Enum.Submitted) {
    return 'Submitted'
  }

  if (
    application.buyer_member?.visits?.length &&
    application.buyer_member.visits.length > 0 &&
    !application.hasDraftBeenEdited
  )
    return 'Booked'

  return 'Draft'
}
